import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { component, ComponentPolicy, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, PotentialInteraction } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { UIField } from '../../canvas/entities/UIField';
import { Pingable } from '../presentation/Pingable';
import { spinnerRemove, spinnerReset, spinnerSpin } from './SpinnerCommands';
import { SpinnerItem } from './SpinnerItem';
import { SpinnerItemField } from './SpinnerItemField';
import { SpinnerLayout } from './SpinnerLayout';
import { spinnerPropertyLayout } from './SpinnerPropertyLayout';

const invocation = defineCommand(
    'spinnerInvocation',
    null,
    (cc, [id, hint]: [string, string?]) => {
        if (hint === 'spin') {
            cc.dispatch(spinnerSpin, [id]);
        }
        else if (hint === 'reset') {
            cc.dispatch(spinnerReset, [id]);
        }
        else if (hint === 'remove') {
            cc.dispatch(spinnerRemove, [id]);
        }
    },
    [],
);

const mutation = defineCommand(
    'spinnerMutation',
    null,
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const spinnerDef = {
    name: 'Spinner',
    implements: [Physical, Snappable, Pingable, Resizable],
    propertyLayout: spinnerPropertyLayout,
    invocation,
    mutation,
};

const spinnerTransformHints = Object.freeze({
    scaleX: true,
    scaleY: true,
    rotate: false,
    translate: true,
    visible: true,
});

const spinnerComponentPolicy = ComponentPolicy.restricted('SpinnerItem');

@component(spinnerDef)
export class Spinner extends RectangularPhysical {

    @property({ type: 'number', default: 140 })
    public width!: number;

    @property({ type: 'number', default: 180 })
    public height!: number;

    @property({ type: 'string', default: 0 })
    public round: string;

    @property({ type: 'number', default: 0 })
    public count: number;

    @property({ type: 'number', default: 0 })
    public value: number;

    @property({ type: 'boolean' })
    public winnerVisible: boolean;

    public get boundary() {
        return true;
    }

    public get shouldSnap() {
        return true;
    }

    public get uiFields(): readonly UIField[] {
        return this.polarize(SpinnerItem)
            .filter(x => !x.hidden)
            .map((tm, i) => SpinnerItemField(this, tm, i));
    }

    protected get policy(): ComponentPolicy {
        return spinnerComponentPolicy;
    }

    @memoUsing('width', 'height', 'round', 'value', 'children')
    public get layout() {
        return SpinnerLayout(this);
    }

    @memoUsing('layout', 'value')
    public get winnerId() {
        const { segments, arm } = this.layout;
        const point = arm.pointAt(0, .5);
        const hit = segments.applied.find(x => x.outline.toClosed().contains(point));
        return hit ? hit.ref : null;
    }

    public queryTransformHints() {
        return spinnerTransformHints;
    }

    public testInteraction({ type, mpt }: PotentialInteraction): [boolean, string] {
        const { spin, reset, remove } = this.layout;
        const rpt = this.toRelative(mpt);
        if (this.localBounds.contains(rpt)) {
            if (type === 'invoke' && rpt.distanceTo(spin.center()) < spin.width / 2) {
                return [true, 'spin'];
            }
            if (type === 'invoke' && reset.contains(rpt)) {
                return [true, 'reset'];
            }
            if (type === 'invoke' && remove.contains(rpt)) {
                return [true, 'remove'];
            }
            if (type !== 'invoke') {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }

    protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {
        w = Math.min(1600, Math.max(400, w));
        h = Math.min(1600, Math.max(400, h));
        super.setTransformationResult(x, y, r, w, h);
    }
}

